/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   PopoverTooltip.tsx
 */

import { useMount } from '@zcode-ui/hook'
import {
  Button,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  cn,
  useDisclosure,
} from '@zcode-ui/shadz-ui'
import React, { type ReactNode, useCallback } from 'react'
import './popoverTooltip.scss'
export interface PopoverTooltipProps {
  align?: 'start' | 'center' | 'end'
  buttonClassName?: string
  children: React.ReactNode
  dataTestId?: string
  forceOpen?: boolean
  hasArrow?: boolean
  id?: string
  label: string
  disabled?: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
  popoverContentClassName?: string
  onClick?: () => void
  richContent?: ReactNode
}

export const PopoverTooltip = ({
  align = 'center',
  buttonClassName,
  children,
  dataTestId,
  forceOpen = false,
  hasArrow = true,
  id,
  label,
  disabled = false,
  popoverContentClassName,
  onClick,
  richContent,
  side = 'bottom',
  ...rest
}: PopoverTooltipProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isMounted, setIsMounted] = React.useState(false)

  useMount(() => setIsMounted(true))

  const handleClick = useCallback(() => {
    onOpen()
    onClick?.()
  }, [onClick, onOpen])

  const content = (
    <PopoverContent
      align={align}
      className={cn(
        'shadow-[0_8px_16px_0_rgba(0,0,0,0.12)] border-gray-100 max-w-[300px] w-auto text-xs text-gray-800 leading-5 px-4 py-3 font-body',
        popoverContentClassName
      )}
      id={id}
      onOpenAutoFocus={(e) => e.preventDefault()}
      onCloseAutoFocus={(e) => e.preventDefault()}
      side={side}
    >
      {richContent ? richContent : label}
      {hasArrow && (
        <>
          <PopoverArrow className="fill-gray-0 stroke-gray-100 stroke-[3px] -translate-y-[1px]" />
          <PopoverArrow className="fill-gray-0 -translate-y-[2px]" />
        </>
      )}
    </PopoverContent>
  )

  return (
    <Popover open={disabled ? false : isOpen || forceOpen} {...rest}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'cursor-default hover:bg-gray-0 h-auto m-0 min-h-auto min-w-auto p-0 w-auto disabled:cursor-default disabled:text-inherit',
            buttonClassName
          )}
          aria-label={label}
          data-testid={dataTestId}
          disabled={disabled}
          onBlur={onClose}
          onClick={handleClick}
          onFocus={onOpen}
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          variant="ghost"
        >
          {children}
        </Button>
      </PopoverTrigger>
      {isMounted ? content : <span className="sr-only">{label}</span>}
    </Popover>
  )
}
